import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BTN_LG } from '../constants/buttons';
import { apiPost } from '../utils/api';
import { FIELD_LABEL_CLASS } from '../constants/inputs';

// Persists the user's intent across the email-verify hop. Stored in
// localStorage (NOT sessionStorage) because the verify link almost always
// opens in a fresh tab where sessionStorage doesn't carry over. Read by
// Login.js (and by post-signup auto-login in dev) to route to the wizard
// or the dashboard. Always cleared on consume so a stale flag doesn't
// hijack a future login.
const SIGNUP_INTENT_KEY = 'sfx_signup_intent';

// ─────────────────────────────────────────────────────────────────────────────
// 🚨 THE BUYER/SUPERVISOR CHOICE IS DELIBERATELY *NOT* MADE ON THIS FORM.
// ─────────────────────────────────────────────────────────────────────────────
// This page NAMES both roles so nobody is told signing up makes them "the
// Coordinator/buyer" (it used to say exactly that, and there is no Coordinator
// role — docs/ROLES.md ruling 9 files it as a possible future seed row). Since
// 2026-10-08 the copy says "buyer/coordinator": a LABEL on the buyer seat, by
// owner ruling, until a real Coordinator role exists. The
// choice itself is made in step 2 of `OnboardingCreateProduction.js` and sent as
// `seat_role` on POST /api/auth/onboarding/create-production.
//
// DO NOT "simplify" by moving the radio here. docs/ROLES.md §F.0 (was §F.2 (c)),
// row "Why the choice is captured on the wizard route, not the signup form":
// a role captured at POST /api/auth/signup and stashed on the `users` row would
// be state PLANTED BY ONE REQUEST AND DETONATED BY ANOTHER — the writer-vs-reader
// shape behind hazard 3 and the `crew_grades.default_role_id` escalation. On the
// wizard route the declaration lives on the same request that opens the
// transaction inserting the seat, so its whole lifetime is one handler. Moving
// the control is a CHANGE OF SECURITY SHAPE, not a refactor.
//
// The copy below must also never imply the two roles are a hierarchy — the
// product owner has ruled seniority a human fact, not a permissions fact, and
// they hold identical capability columns today (docs/ROLES.md §B).

export default function Signup() {
  useEffect(() => { document.documentElement.classList.add('dark'); }, []);

  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [signupIntent, setSignupIntent] = useState('join_existing');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // Bumped on every Create account press without the tick. Non-zero = the row is
  // red; the value keys the row so each press re-runs the shake.
  const [termsNudge, setTermsNudge] = useState(0);

  const passwordValid = password.length >= 8;
  const passwordsMatch = password === confirmPassword;
  const termsMissing = termsNudge > 0 && !agreedToTerms;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!passwordValid) { setError('Password must be at least 8 characters'); return; }
    if (!passwordsMatch) { setError('Passwords do not match'); return; }
    if (!agreedToTerms) { setTermsNudge(n => n + 1); return; }

    setSubmitting(true);
    try {
      // Stash the intent so post-verify (or post-login) can route correctly.
      // Cleared by the wizard or the dashboard once consumed.
      localStorage.setItem(SIGNUP_INTENT_KEY, signupIntent);

      const res = await apiPost('/api/auth/signup', {
        display_name: displayName,
        email,
        password,
        signup_intent: signupIntent === 'create_production' ? 'create_production' : null,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Signup failed');

      // Dev mode auto-verifies + auto-logs-in (server returns autoVerified:true).
      // Skip the "check your email" screen and route straight to wizard or dashboard.
      if (data.autoVerified) {
        if (signupIntent === 'create_production') {
          navigate('/onboarding/new-production', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
        return;
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResent(false);
    try {
      const res = await apiPost('/api/auth/resend-verification', { email });
      if (res.ok) setResent(true);
    } catch (err) {
      // Silently fail
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            CREW FORGE
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {success ? 'Almost there!' : 'Create your account'}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 sm:p-8">
          {success ? (
            <div className="text-center space-y-4">
              <div className="w-12 h-12 mx-auto bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Check your email</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                We've sent a verification link to <strong className="text-gray-700 dark:text-gray-300">{email}</strong>. Click the link to activate your account.
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500">The link expires in 24 hours.</p>
              <div className="pt-2">
                {resent ? (
                  <p className="text-sm text-green-600 dark:text-green-400">Verification email resent!</p>
                ) : (
                  <button
                    onClick={handleResend}
                    disabled={resending}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                  >
                    {resending ? 'Sending...' : "Didn't receive it? Resend"}
                  </button>
                )}
              </div>
              <Link to="/login" className="inline-block mt-4 text-sm text-blue-600 dark:text-blue-400 hover:underline">
                Go to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="displayName" className={FIELD_LABEL_CLASS}>Full Name</label>
                <input id="displayName" type="text" required autoFocus value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="Your full name" />
              </div>

              <div>
                <label htmlFor="email" className={FIELD_LABEL_CLASS}>Email</label>
                <input id="email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="you@example.com" />
              </div>

              <div>
                <label htmlFor="password" className={FIELD_LABEL_CLASS}>Password</label>
                <input id="password" type="password" required autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="At least 8 characters" />
                {password.length > 0 && (
                  <p className={`mt-1 text-xs ${passwordValid ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    {passwordValid ? 'Password length is good' : `${8 - password.length} more character${8 - password.length !== 1 ? 's' : ''} needed`}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className={FIELD_LABEL_CLASS}>Confirm Password</label>
                <input id="confirmPassword" type="password" required autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="Confirm your password" />
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">Passwords do not match</p>
                )}
              </div>

              <div>
                <label className={FIELD_LABEL_CLASS}>
                  How will you use Crew Forge?
                </label>
                <div className="space-y-2">
                  <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                    signupIntent === 'join_existing'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}>
                    <input
                      type="radio"
                      name="signupIntent"
                      value="join_existing"
                      checked={signupIntent === 'join_existing'}
                      onChange={(e) => setSignupIntent(e.target.value)}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">Joining a team</span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        A standard user account. Choose this if you expect to be added to a department.
                        It's the usual route for everyone except the person setting up the production.
                      </span>
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                    signupIntent === 'create_production'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}>
                    <input
                      type="radio"
                      name="signupIntent"
                      value="create_production"
                      checked={signupIntent === 'create_production'}
                      onChange={(e) => setSignupIntent(e.target.value)}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">Setting up a new production only</span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        I'm the buyer/coordinator or supervisor, the one responsible for creating the
                        production and its first department, and inviting my team.
                      </span>
                    </div>
                  </label>
                </div>
                {signupIntent === 'create_production' && (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    You choose <strong className="font-medium text-gray-700 dark:text-gray-300">buyer/coordinator</strong>
                    {' or '}
                    <strong className="font-medium text-gray-700 dark:text-gray-300">supervisor</strong>
                    {' '}in the next step. It applies to the department you create and gives you nothing
                    in anyone else&apos;s.
                  </p>
                )}
              </div>

              <div key={termsNudge} data-testid="signup-terms-row"
                className={termsMissing ? 'motion-safe:animate-shake' : undefined}>
              <div className="flex items-start gap-2">
                <input
                  id="agreeTerms"
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  aria-invalid={termsMissing || undefined}
                  aria-describedby={termsMissing ? 'agreeTerms-error' : undefined}
                  className={`mt-1 h-4 w-4 rounded text-blue-600 focus:ring-blue-500 ${
                    termsMissing
                      ? 'border-red-500 dark:border-red-400 ring-2 ring-red-500 dark:ring-red-400'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                <label htmlFor="agreeTerms" className={`text-xs leading-relaxed ${
                  termsMissing ? 'text-red-600' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  I agree to the{' '}
                  <Link to="/terms-of-service" target="_blank" className="text-blue-600 dark:text-blue-400 hover:underline">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy-policy" target="_blank" className="text-blue-600 dark:text-blue-400 hover:underline">Privacy Policy</Link>
                </label>
              </div>
              {termsMissing && (
                // `text-red-600` is bare on purpose: index.css's html.dark rule delivers its dark value.
                <p id="agreeTerms-error" role="alert" className="mt-1 ml-6 text-xs font-medium text-red-600">
                  Please tick to agree before creating your account.
                </p>
              )}
              </div>

              {/* Not disabled on the missing tick: pressing it is what shakes the row above. */}
              <button type="submit" disabled={submitting || !passwordValid || !passwordsMatch}
                className={`${BTN_LG.primary} w-full justify-center`}>
                {submitting ? 'Creating account...' : 'Create account'}
              </button>
            </form>
          )}
        </div>

        {!success && (
          <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">Sign in</Link>
          </p>
        )}

        {/* Legal links */}
        <p className="mt-4 text-center text-xs text-gray-400 dark:text-gray-500">
          By signing up, you agree to our{' '}
          <Link to="/terms-of-service" className="hover:underline">Terms of Service</Link>
          {' and '}
          <Link to="/privacy-policy" className="hover:underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}
